import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Search, AlertTriangle, AlertCircle, Info, Check, X, ArrowRight, User } from 'lucide-react';
import api from '../../api/client';
import toast from 'react-hot-toast';

export default function IntakeCreate() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('search'); // search | form
  const [searchPhone, setSearchPhone] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linkedContact, setLinkedContact] = useState(null);

  // Form fields
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', email: '', dob: '',
    address_line1: '', city: '', state: '', zip: '',
    preferred_language: '', date_of_loss: '', sol_date: '', incident_type: '',
    incident_summary: '', primary_injury: [], currently_treating: false,
    treating_provider: '', referral_source: '', referring_attorney: '',
    intake_notes: '', assigned_attorney: '', intake_by: '',
  });
  const [saving, setSaving] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [dismissedDups, setDismissedDups] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [solManual, setSolManual] = useState(false);
  const debounceRef = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Fetch tenant users for attorney/intake_by dropdowns
  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data.users || r.data || [])).catch(() => {});
  }, []);

  // SOL auto-calculation
  useEffect(() => {
    if (solManual) return;
    const sol = calcSOL(form.date_of_loss, form.state, form.incident_type);
    setForm(f => f.sol_date === sol ? f : { ...f, sol_date: sol });
  }, [form.date_of_loss, form.state, form.incident_type, solManual]);

  // Phase 1: Search by phone
  const handleSearch = async () => {
    if (!searchPhone.trim()) return;
    setSearching(true);
    try {
      const res = await api.post('/intake/check-duplicates', { phone: searchPhone });
      setSearchResults(res.data.matches || []);
    } catch { /* ignore */ }
    setSearching(false);
  };

  const skipToForm = () => {
    setPhase('form');
    if (searchPhone) set('phone', searchPhone);
  };

  const useContact = (match) => {
    setLinkedContact(match.record);
    setPhase('form');
    if (searchPhone) set('phone', searchPhone);
  };

  // Phase 2: Real-time duplicate detection
  const checkDuplicates = useCallback(async (fields) => {
    const { phone, email, first_name, last_name, dob } = fields;
    if (!phone && !email && !first_name) return;
    try {
      const res = await api.post('/intake/check-duplicates', { phone, email, first_name, last_name, dob });
      setDuplicates(res.data.matches || []);
    } catch { /* ignore */ }
  }, []);

  const triggerDupCheck = useCallback((updatedForm) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => checkDuplicates(updatedForm), 500);
  }, [checkDuplicates]);

  const handleFieldChange = (key, value) => {
    const updated = { ...form, [key]: value };
    setForm(updated);
    if (['phone', 'email', 'first_name', 'last_name', 'dob'].includes(key)) {
      triggerDupCheck(updated);
    }
  };

  const handleSave = async () => {
    if (!form.first_name || !form.last_name || !form.phone || !form.date_of_loss || !form.incident_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      // Get the intake record type ID and field defs
      const rtRes = await api.get('/admin/record-types');
      const intakeRt = (rtRes.data.record_types || []).find(rt => rt.slug === 'intake');
      if (!intakeRt) { toast.error('Intake record type not found'); setSaving(false); return; }

      const fieldsRes = await api.get(`/admin/fields?record_type_id=${intakeRt.id}`);
      const fieldDefs = fieldsRes.data.fields || [];
      const fieldMap = {};
      fieldDefs.forEach(f => { fieldMap[f.name] = f.id; });

      // Build field_data keyed by UUID
      const fieldData = {};
      for (const [key, val] of Object.entries(form)) {
        if (fieldMap[key] && val !== '' && val !== null && val !== undefined) {
          fieldData[fieldMap[key]] = val;
        }
      }

      // Set linked contact if available
      if (linkedContact && fieldMap.contact_id) {
        fieldData[fieldMap.contact_id] = linkedContact.id;
      }

      const res = await api.post('/records', {
        record_type_id: intakeRt.id,
        field_data: fieldData,
      });

      toast.success('Intake created');
      navigate(`/records/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create intake');
    }
    setSaving(false);
  };

  // Active duplicates (not dismissed)
  const activeDups = duplicates.filter(d => !dismissedDups.has(d.record.id));

  const INCIDENT_TYPES = ["Motor Vehicle Accident", "Slip & Fall", "Dog Bite", "Wrongful Death", "Workers Comp", "Medical Malpractice", "Product Liability", "Assault", "Divorce", "Custody", "Protective Order", "Real Estate", "Other"];
  const REFERRAL_SOURCES = ["Google/Web", "TV Ad", "Radio", "Billboard", "Referral - Attorney", "Referral - Client", "Referral - Medical Provider", "Social Media", "Walk-In", "Phone", "Chat", "Other"];
  const LANGUAGES = ["English", "Spanish", "Mandarin", "Vietnamese", "Korean", "Arabic", "Other"];
  const PRIMARY_INJURIES = ["Cervical Strain", "Lumbar Strain", "Herniated Disc", "Shoulder Injury", "Knee Injury", "Head Injury / TBI", "Soft Tissue", "Broken Bone / Fracture", "Internal Injuries", "Lacerations", "Burns", "Emotional Distress", "Wrongful Death", "Other"];
  const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
          <ClipboardList className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">New Intake</h1>
          <p className="text-sm text-gray-500">Universal lead intake form</p>
        </div>
      </div>

      {/* Phase 1: Search */}
      {phase === 'search' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Search for existing contact first</h2>
          <p className="text-sm text-gray-500 mb-4">Check if this person is already in the system before creating a new intake.</p>
          <div className="flex gap-2 mb-4">
            <input type="tel" value={searchPhone} onChange={e => setSearchPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Phone number..." className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            <button onClick={handleSearch} disabled={searching}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              <Search className="w-4 h-4" /> {searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div className="space-y-2 mb-4">
              <p className="text-sm font-medium text-amber-700 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Existing records found:</p>
              {searchResults.map(m => (
                <div key={m.record.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{m.record.title}</span>
                    <span className="text-xs text-gray-500 ml-2">{m.record.record_type}</span>
                    <span className="text-xs text-amber-600 ml-2">({m.type} match: {m.signal})</span>
                  </div>
                  <button onClick={() => useContact(m)}
                    className="text-xs px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                    Link & Continue
                  </button>
                </div>
              ))}
            </div>
          )}

          {searchResults.length === 0 && searchPhone && !searching && (
            <p className="text-sm text-green-600 mb-4 flex items-center gap-1"><Check className="w-4 h-4" /> No matches found.</p>
          )}

          <button onClick={skipToForm} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            Skip search, create new intake →
          </button>
        </div>
      )}

      {/* Phase 2: Intake Form */}
      {phase === 'form' && (
        <div className="space-y-4">
          {/* Linked contact banner */}
          {linkedContact && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
              <User className="w-4 h-4 text-green-600" />
              <span className="text-green-700">Linked to: <strong>{linkedContact.title}</strong> ({linkedContact.record_type})</span>
              <button onClick={() => setLinkedContact(null)} className="ml-auto text-green-600 hover:text-red-500"><X className="w-4 h-4" /></button>
            </div>
          )}

          {/* Duplicate warnings */}
          {activeDups.map(dup => (
            <DuplicateWarning key={dup.record.id} match={dup}
              onLink={() => { setLinkedContact(dup.record); setDismissedDups(prev => new Set([...prev, dup.record.id])); }}
              onDismiss={() => setDismissedDups(prev => new Set([...prev, dup.record.id]))}
            />
          ))}

          {/* Form card */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase">Client Information</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First Name *" value={form.first_name} onChange={v => handleFieldChange('first_name', v)} />
              <Field label="Last Name *" value={form.last_name} onChange={v => handleFieldChange('last_name', v)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <PhoneField label="Phone *" value={form.phone} onChange={v => handleFieldChange('phone', v)} />
              <Field label="Email" value={form.email} onChange={v => handleFieldChange('email', v)} type="email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date of Birth" value={form.dob} onChange={v => handleFieldChange('dob', v)} type="date" />
              <SelectField label="Preferred Language" value={form.preferred_language} onChange={v => set('preferred_language', v)} options={LANGUAGES} />
            </div>
            <Field label="Address" value={form.address_line1} onChange={v => set('address_line1', v)} />
            <div className="grid grid-cols-3 gap-3">
              <Field label="City" value={form.city} onChange={v => set('city', v)} />
              <SelectField label="State" value={form.state} onChange={v => set('state', v)} options={US_STATES} />
              <Field label="ZIP Code" value={form.zip} onChange={v => set('zip', v)} />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase">Incident Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date of Incident *" value={form.date_of_loss} onChange={v => set('date_of_loss', v)} type="date" />
              <Field label="Statute of Limitations" value={form.sol_date} onChange={v => { setSolManual(true); set('sol_date', v); }} type="date" />
            </div>
            <SelectField label="Incident Type *" value={form.incident_type} onChange={v => set('incident_type', v)} options={INCIDENT_TYPES} />
            <Field label="Incident Summary" value={form.incident_summary} onChange={v => set('incident_summary', v)} multiline />
            <MultiSelectField label="Primary Injury / Issue" value={form.primary_injury} onChange={v => set('primary_injury', v)}
              options={PRIMARY_INJURIES} />
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.currently_treating} onChange={e => set('currently_treating', e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              <label className="text-sm text-gray-700">Currently treating</label>
            </div>
            {form.currently_treating && (
              <Field label="Treating Provider" value={form.treating_provider} onChange={v => set('treating_provider', v)} />
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase">Lead Tracking</h3>
            <div className="grid grid-cols-2 gap-3">
              <SelectField label="Referral Source" value={form.referral_source} onChange={v => set('referral_source', v)} options={REFERRAL_SOURCES} />
              <Field label="Referring Attorney" value={form.referring_attorney} onChange={v => set('referring_attorney', v)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <UserSelect label="Assigned Attorney" value={form.assigned_attorney} onChange={v => set('assigned_attorney', v)} users={users} />
              <UserSelect label="Intake Taken By" value={form.intake_by} onChange={v => set('intake_by', v)} users={users} />
            </div>
            <Field label="Intake Notes" value={form.intake_notes} onChange={v => set('intake_notes', v)} multiline />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button onClick={() => navigate(-1)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              {saving ? 'Creating...' : 'Create Intake'} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───

function Field({ label, value, onChange, type = 'text', multiline = false }) {
  const cls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {multiline ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={2} className={cls} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} className={cls} />
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
        <option value="">Select...</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function calcSOL(dateOfLoss, state, incidentType) {
  if (!dateOfLoss) return '';
  const d = new Date(dateOfLoss);
  const type = (incidentType || '').toLowerCase();
  let years = 2; // default
  if (type.includes('workers')) years = 1;
  if (type.includes('family') || type.includes('divorce') || type.includes('custody')) return '';
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().split('T')[0];
}

function formatPhone(val) {
  const digits = val.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
  return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
}

function PhoneField({ label, value, onChange }) {
  const cls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input type="tel" value={value}
        onChange={e => onChange(formatPhone(e.target.value))}
        placeholder="(555) 123-4567" className={cls} />
    </div>
  );
}

function MultiSelectField({ label, value, onChange, options }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    onChange(selected.includes(opt) ? selected.filter(v => v !== opt) : [...selected, opt]);
  };
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selected.map(s => (
            <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">
              {s}
              <button type="button" onClick={() => toggle(s)} className="text-indigo-400 hover:text-indigo-600">&times;</button>
            </span>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
        {options.map(opt => (
          <label key={opt} className="flex items-center gap-2 text-sm text-gray-700 py-0.5 cursor-pointer hover:bg-gray-50 rounded px-1">
            <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );
}

function UserSelect({ label, value, onChange, users }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
        <option value="">Select...</option>
        {users.map(u => (
          <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
        ))}
      </select>
    </div>
  );
}

function DuplicateWarning({ match, onLink, onDismiss }) {
  const colors = {
    hard: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: AlertTriangle, iconColor: 'text-red-500' },
    soft: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: AlertCircle, iconColor: 'text-amber-500' },
    suggestion: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: Info, iconColor: 'text-blue-500' },
  };
  const c = colors[match.type] || colors.suggestion;
  const Icon = c.icon;

  return (
    <div className={`p-3 rounded-lg border ${c.bg} ${c.border}`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 ${c.iconColor}`} />
        <div className="flex-1">
          <p className={`text-sm font-medium ${c.text}`}>
            {match.type === 'hard' ? 'Stop — This person may already be in the system:' :
             match.type === 'soft' ? 'Similar contact found:' : 'Did you mean:'}
          </p>
          <p className="text-sm text-gray-700 mt-1">
            <strong>{match.record.title}</strong> — {match.record.record_type}
            <span className="text-gray-400 ml-1">({match.signal} match)</span>
          </p>
          <div className="flex gap-2 mt-2">
            <button onClick={onLink} className="text-xs px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700">
              Yes, link to this person
            </button>
            <button onClick={onDismiss} className="text-xs px-3 py-1 border border-gray-300 text-gray-600 rounded hover:bg-gray-50">
              No, different person
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
